/**
 * Аналитика клиента (I3): `startAnalytics()` из `main.tsx`, `analytics()` — для остальных модулей (шаринг).
 * Модуль не импортирует `app/index` (цикл через окна): состояние читает из `app/store`.
 */
import { appStore } from "../app/store";
import { isTestAccount } from "../app/test-account";
import { browserPageContext, createAnalytics, fetchWebsiteId, isQaVisit, type Hit } from "./analytics";
import { createInteractionGate } from "./gate";
import { createRecorder } from "./recorder";
import { BEAT_MS } from "./session";
import { createTracker, type Tracker } from "./tracker";

export { createAnalytics, fetchWebsiteId, type Analytics } from "./analytics";
export { createTracker, type Tracker, type TrackerDeps } from "./tracker";

let current: Tracker | null = null;

/** Трекер текущей сессии; до `startAnalytics` — пустышка (шаринг в тестах и `?mock=1`). */
export function analytics(): Pick<
  Tracker,
  | "share"
  | "friendInvite"
  | "waveLogged"
  | "offerView"
  | "purchaseClick"
  | "purchaseResult"
  | "reviveOffer"
  | "starterOffer"
  | "reviveStarted"
  | "tasksOpen"
  | "taskClaim"
  | "writeAccessAsk"
  | "writeAccess"
  | "homeScreenAsk"
  | "homeScreenAdded"
  | "feedbackSent"
  | "parcelTap"
  | "parcelMissed"
  | "appHidden"
  | "appVisible"
> {
  const noop = () => {};
  return (
    current ?? {
      share: noop,
      friendInvite: noop,
      waveLogged: noop,
      offerView: noop,
      purchaseClick: noop,
      purchaseResult: noop,
      reviveOffer: noop,
      starterOffer: noop,
      reviveStarted: noop,
      tasksOpen: noop,
      taskClaim: noop,
      writeAccessAsk: noop,
      writeAccess: noop,
      homeScreenAsk: noop,
      homeScreenAdded: noop,
      feedbackSent: noop,
      parcelTap: noop,
      parcelMissed: noop,
      appHidden: noop,
      appVisible: noop,
    }
  );
}

/** Случайный id сессии: `crypto.randomUUID` есть только на https и localhost, а игре нужен и http-стенд. */
function randomSid(): string {
  return Math.random().toString(36).slice(2, 10).padEnd(8, "0");
}

/** Запустить аналитику: событие открытия, слежение за игроком, повтор отправки при возвращении сети. */
export function startAnalytics(): Tracker {
  let websiteId: string | null = null;
  let recorder: ReturnType<typeof createRecorder> | null = null;
  const core = createAnalytics({
    fetch: (...a) => fetch(...a),
    website: async () => (websiteId = isQaVisit(location.search) ? null : await fetchWebsiteId()),
    page: () => browserPageContext(appStore.get().startParam),
    // рука A/B-теста масштаба поля — у всех событий после входа
    common: () => ({ scale: appStore.get().player?.player.fieldScale ?? null }),
    onCache: () => startRecorder(),
  });
  const storage = ((): Storage | null => {
    try {
      return localStorage;
    } catch {
      return null; // приватный режим или заблокированное хранилище
    }
  })();
  // в браузере — только после первого действия игрока (обходчики с JS экрана не касаются)
  const gated = createInteractionGate<Hit>({ open: appStore.get().miniApp, target: window, send: core.hit });
  const send = (hit: Hit): void => {
    if (!isTestAccount(appStore.get().player?.player ?? null, storage)) gated(hit);
  };
  const track: typeof core.track = (event) => send({ kind: "event", event });
  // запись визита (ISSUE-147): после первой принятой отправки и только у известного не тестового игрока
  const startRecorder = (): void => {
    const player = appStore.get().player?.player ?? null;
    if (!websiteId || !player || isTestAccount(player, storage)) return;
    recorder ??= createRecorder({ doc: document, win: window as { umami?: unknown }, websiteId, cache: core.cache });
    recorder.start();
  };
  const tracker = createTracker({ track, now: Date.now, storage, newId: randomSid });
  current = tracker;
  const s = appStore.get();
  send({ kind: "pageview" }); // один на открытие: путь меняется только replaceState языка, повторно не шлём
  tracker.appOpen(s.platform, s.startParam ? s.startParam.slice(0, 64) : null);
  appStore.subscribe((state, prev) => {
    if (state.player && state.player !== prev.player) {
      tracker.playerChanged(prev.player, state.player, state.auth.history ?? undefined);
      startRecorder();
    }
  });
  window.addEventListener("online", () => core.retry());
  setInterval(tracker.sessionBeat, BEAT_MS);
  return tracker;
}
